// src/payment/components/StripePayment.js
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { 
  LuArrowLeft, 
  LuX, 
  LuCreditCard, 
  LuShieldCheck, 
  LuRefreshCw, 
  LuCircleAlert,
  LuGlobe,
  LuSparkles
} from 'react-icons/lu';
import PaymentSummary from './PaymentSummary';
import './StripePayment.css';

// ==========================================
// COMPONENTE INTERNO (CON HOOKS DE STRIPE)
// ==========================================
const StripeForm = ({ 
  resumen, 
  loading, 
  error: externalError, 
  onPay, 
  onBack, 
  onClose,
  isStripeReady
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [localError, setLocalError] = useState(null);

  // 💡 Cálculo del monto en Dólares ($us)
  // 🟢 FIX (corregido): `resumen.monto` es el precio BASE puro (así lo exige
  // la validación del backend, ver adminStripeConfigController.js) — ya NO
  // se sobreescribe con el total. El total con fee vive aparte en
  // `resumen.montoConFee`, calculado en PaymentModal solo para mostrarlo.
  // Ejemplo: paquete 100 Bs a 10 Bs/USD, fee fijo de 5 USD (sin convertir):
  //   Base:  100 Bs  -> 10.00 $us
  //   Fee:                +5.00 $us  (fijo, ya en USD, no se convierte)
  //   Total: 150 Bs  -> 15.00 $us
  const montoBaseBOB = parseFloat(resumen?.monto) || 0; // precio base puro
  const montoTotalBOB = parseFloat(resumen?.montoConFee ?? resumen?.monto) || 0; // base + fee, si ya cargó
  const tasaCambio = parseFloat(resumen?.paqueteSeleccionado?.conversion_rate) || 6.96;
  const montoUSD = (montoTotalBOB / tasaCambio).toFixed(2); // total en USD (base + fee)

  // 🟢 FIX: feeAplicado llega en Bs. desde PaymentModal (moneda consistente
  // con `monto`, que ya incluye el fee). Antes se mostraba el número crudo
  // etiquetado como "$us" sin convertir, lo cual además no coincidía con el
  // total en dólares (que se calcula aparte). Ahora se convierte con la
  // misma tasa que usa montoUSD, así el desglose sí cuadra con el total.
  const feeAplicadoBOB = parseFloat(resumen?.feeAplicado) || 0;
  const feeAplicadoUSD = (feeAplicadoBOB / tasaCambio).toFixed(2);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError(null);

    if (!stripe || !elements) {
      setLocalError('El sistema de pago no está listo. Por favor, reintenta en un momento.');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setLocalError('Error al obtener los datos de la tarjeta.');
      return;
    }

    try {
      // 🔧 Pasamos `stripe` (la instancia ya viva en este <Elements>, la misma
      // que creó el CardElement) para que confirmCardPayment se haga con la
      // instancia correcta y no con una nueva.
      await onPay(cardElement, stripe);
    } catch (err) {
      const errorMsg = err.message || 'Error al procesar el pago con tarjeta';
      setLocalError(errorMsg);
    }
  };

  const isProcessing = loading?.registering || loading?.generating;
  const error = externalError || localError;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !isProcessing) {
      onClose();
    }
  };

  return (
    <div className="stripe-modal-overlay" onClick={handleOverlayClick}>
      <div className="stripe-modal-card">
        
        {/* Barra superior de navegación */}
        <div className="stripe-modal-nav">
          <button className="stripe-nav-back-btn" onClick={onBack} type="button">
            <LuArrowLeft /> Volver
          </button>
          <button className="stripe-nav-close-btn" onClick={onClose} type="button" aria-label="Cerrar">
            <LuX />
          </button>
        </div>

        {/* Encabezado */}
        <div className="stripe-modal-header">
          <div className="stripe-header-title">
            <LuCreditCard className="stripe-title-icon" />
            <h2>Pago Internacional</h2>
          </div>
          <p className="stripe-modal-subtitle">Procesado de forma 100% segura mediante Stripe</p>
        </div>

        {/* Resumen de conversión BOB -> USD */}
        <div className="stripe-usd-card">
          <div className="stripe-usd-row">
            <span className="usd-label">Monto de inscripción:</span>
            <span className="usd-value-bob">{montoBaseBOB.toFixed(2)} Bs.</span>
          </div>
          
          {/* Muestra de Tasa de Procesamiento si existe en el resumen */}
          {feeAplicadoBOB > 0 && (
            <div className="stripe-usd-row">
              <span className="usd-label">Tasa de transacción / procesamiento:</span>
              <span className="usd-value-bob">+{feeAplicadoUSD} $us</span>
            </div>
          )}

          <div className="stripe-usd-row total">
            <span className="usd-label-total"><LuGlobe /> Total a cobrar en Dólares:</span>
            <span className="usd-value-total">{montoUSD} $us</span>
          </div>

          <div className="usd-exchange-badge">
            <LuSparkles /> Tasa de cambio aplicada: 1 USD = {tasaCambio} BOB
          </div>
        </div>


        {/* Formulario de Tarjeta */}
        <form onSubmit={handleSubmit} className="stripe-payment-form">
          <div className="stripe-form-group">
            <label className="stripe-input-label">
              <span>Datos de la Tarjeta</span>
              <span className="cards-accepted">Visa / Mastercard / Amex</span>
            </label>
            
            <div className="stripe-card-element-wrapper">
              <CardElement 
                options={{
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#ffffff',
                      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
                      fontWeight: '500',
                      '::placeholder': { color: '#64748b' },
                      iconColor: '#00c6fb',
                    },
                    invalid: { 
                      color: '#ff2a55',
                      iconColor: '#ff2a55',
                    },
                  },
                  hidePostalCode: true,
                }}
              />
            </div>
          </div>

          {error && (
            <div className="stripe-error-banner">
              <LuCircleAlert className="error-icon" />
              <span>{error}</span>
            </div>
          )}

          <button 
            type="submit" 
            className="stripe-submit-btn"
            disabled={!stripe || !isStripeReady || isProcessing}
          >
            {isProcessing ? (
              <>
                <LuRefreshCw className="stripe-spin-icon" />
                Procesando Pago Seguro...
              </>
            ) : (
              <>
                <LuCreditCard /> Pagar {montoUSD} $us
              </>
            )}
          </button>

          <div className="stripe-security-footer">
            <LuShieldCheck className="security-icon" />
            <span>Encriptación SSL de 256 bits. Tus datos bancarios están protegidos.</span>
          </div>
        </form>

      </div>
    </div>
  );
};

StripeForm.propTypes = {
  resumen: PropTypes.object.isRequired,
  loading: PropTypes.shape({
    generating: PropTypes.bool,
    registering: PropTypes.bool,
  }),
  error: PropTypes.string,
  onPay: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  stripePromise: PropTypes.object,
  isStripeReady: PropTypes.bool,
};

// ==========================================
// COMPONENTE WRAPPER (CON ELEMENTS PROVIDER)
// ==========================================
const StripePayment = (props) => {
  const [stripePromise, setStripePromise] = useState(null);
  const [isStripeReady, setIsStripeReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isDev = process.env.NODE_ENV === 'development';

  useEffect(() => {
    const initializeStripe = async () => {
      try {
        setLoading(true);
        setError(null);

        const { StripeAdapter } = await import('../adapters/StripeAdapter');
        const slug = props.resumen?.slug;
        
        if (!slug && !isDev) {
          throw new Error('No se encontró slug del evento');
        }

        const adapter = new StripeAdapter(slug || 'demo-event', props.resumen);
        const stripe = await adapter.initialize();
        
        const promise = Promise.resolve(stripe);
        setStripePromise(promise);
        setIsStripeReady(true);
      } catch (err) {
        console.error('[StripePayment] Error cargando Stripe:', err);
        // En Desarrollo no bloqueamos la maquetación si Stripe no inicia
        if (isDev) {
          setIsStripeReady(true);
        } else {
          setError(err.message || 'Error al cargar el sistema de pagos internacional');
        }
      } finally {
        setLoading(false);
      }
    };

    initializeStripe();
  }, [props.resumen?.slug, props.resumen, isDev]);

  if (loading) {
    return (
      <div className="stripe-modal-overlay">
        <div className="stripe-modal-card state-box">
          <LuRefreshCw className="stripe-spinner-icon" />
          <h3>Cargando Pasarela de Pago Seguro...</h3>
          <p>Conectando con Stripe internacional.</p>
        </div>
      </div>
    );
  }

  if (error && !isDev) {
    return (
      <div className="stripe-modal-overlay">
        <div className="stripe-modal-card state-box error">
          <LuCircleAlert className="stripe-error-icon" />
          <h3>Error en la Pasarela</h3>
          <p>{error}</p>
          <button className="stripe-retry-btn" onClick={() => window.location.reload()}>
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <StripeForm 
        {...props} 
        stripePromise={stripePromise}
        isStripeReady={isStripeReady}
      />
    </Elements>
  );
};

StripePayment.propTypes = {
  resumen: PropTypes.object.isRequired,
  loading: PropTypes.shape({
    generating: PropTypes.bool,
    registering: PropTypes.bool,
  }),
  error: PropTypes.string,
  onPay: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default StripePayment;
